import { useCallback } from 'react';
import { useTypedAppForm } from '@gonfalon/forms-experimental/hooks';
import {
  Button,
  Heading,
  Label,
  Text,
  TextArea,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { DatasetPicker } from './DatasetPicker';
import type { AddToDatasetDraft, AnnotationBody, AnnotationDraft, Correctness, DatasetOption } from './types';

export interface ReviewFormProps {
  traceId: string;
  spanId: string;
  projectKey: string;
  envKey: string;
  /** The span's actual generated output, if any — seeds "Insert actual output" on an incorrect mark. */
  actualOutput?: string;
  /** Phase 1: defaults to logging; a real host persists the annotation via PUT <annotation path>. */
  onSubmit?: (draft: AnnotationDraft) => void;
  /** Phase 1: defaults to logging; a real host calls POST .../datasets/{datasetId}/rows. */
  onAddToDataset?: (draft: AddToDatasetDraft) => void;
}

const DEFAULT_VALUES: AnnotationBody = {
  correctness: undefined,
  explanation: '',
  expectedOutputOverride: '',
};

const logAnnotation = (draft: AnnotationDraft) => {
  // eslint-disable-next-line no-console
  console.log('[annotations] PUT <annotation path> body', draft);
};

const logAddToDataset = (draft: AddToDatasetDraft) => {
  // eslint-disable-next-line no-console
  console.log('[annotations] POST .../datasets/{datasetId}/rows body', draft);
};

export const ReviewForm = ({
  traceId,
  spanId,
  projectKey,
  envKey,
  actualOutput,
  onSubmit = logAnnotation,
  onAddToDataset = logAddToDataset,
}: ReviewFormProps) => {
  const form = useTypedAppForm<AnnotationBody>({
    defaultValues: DEFAULT_VALUES,
    onSubmit: ({ value }: { value: AnnotationBody }) => {
      onSubmit({ traceId, spanId, ...value });
    },
  });

  const handleSelectDataset = useCallback(
    (dataset: DatasetOption) => {
      onAddToDataset({ traceId, spanId, datasetId: dataset.id, envKey, overwriteExisting: false });
    },
    [onAddToDataset, traceId, spanId, envKey],
  );

  return (
    <form.AppForm>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '0.5rem 0' }}>
        <Heading level={3}>Review</Heading>

        <form.AppField name="correctness">
          {(field) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <Label>Correctness</Label>
              <ToggleButtonGroup
                selectionMode="single"
                selectedKeys={field.state.value !== undefined ? [field.state.value] : []}
                onSelectionChange={(keys) => {
                  const key = [...keys][0];
                  const next: Correctness | undefined = key === 'correct' || key === 'incorrect' ? key : undefined;
                  field.handleChange(next);
                  if (next !== 'incorrect') {
                    // The override only exists under an incorrect verdict (correct: the expected output is the
                    // actual output; no verdict: nothing to correct) — hidden stale text must not submit.
                    form.setFieldValue('expectedOutputOverride', '');
                  }
                }}
              >
                <ToggleButton id="correct" aria-label="Correct">
                  <Icon name="thumb-up-outline" size="small" />
                  Correct
                </ToggleButton>
                <ToggleButton id="incorrect" aria-label="Incorrect">
                  <Icon name="thumb-down-outline" size="small" />
                  Incorrect
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          )}
        </form.AppField>

        <form.AppField name="explanation">
          {(field) => (
            <TextField value={field.state.value ?? ''} onChange={(value) => field.handleChange(value)}>
              <Label>Explanation</Label>
              <TextArea placeholder="Optional context for the next reviewer." />
            </TextField>
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.correctness}>
          {(correctness) =>
            correctness === 'incorrect' && (
              <form.AppField name="expectedOutputOverride">
                {(field) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Label>Expected output</Label>
                      <Button
                        variant="minimal"
                        size="small"
                        isDisabled={!actualOutput}
                        onPress={() => form.setFieldValue('expectedOutputOverride', actualOutput ?? '')}
                      >
                        Insert actual output
                      </Button>
                    </div>
                    <TextField
                      aria-label="Expected output"
                      value={field.state.value ?? ''}
                      onChange={(value) => field.handleChange(value)}
                    >
                      <TextArea />
                      <Text slot="description">
                        Optional. Without a correction, a dataset row promoted from this span keeps the incorrect output
                        as its expected output.
                      </Text>
                    </TextField>
                  </div>
                )}
              </form.AppField>
            )
          }
        </form.Subscribe>

        <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem', paddingTop: '0.5rem' }}>
          <form.SubmitButton>Save review</form.SubmitButton>
          <DatasetPicker projectKey={projectKey} onSelect={handleSelectDataset} />
        </div>
      </div>
    </form.AppForm>
  );
};
